import React, { ReactElement, useState } from 'react';
import TextInput from '../../TextInput';
import {
  DiscountDetails,
  DiscountText,
  ErrorDisplay,
  PromoCodeContainer,
  PromoInputContainer,
  SummaryAmountText,
} from './PromoCodeStyles';
import { promoCodeCheck } from '../../../sdk';
import { getBackgroundTheme, getBorders } from '../../../style';
import { handlePaymentVysionEvent } from '../../../utils/vysion/payment';
import { VYSION_ACTIONS } from '../../../utils/vysion';
import { PaymentOnFocusVysionEventsEnum } from '../../../utils/vysion/payment/onFocus';
import { PaymentClickVysionEventsEnum } from '../../../utils/vysion/payment/click';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { isDarkMode } from 'utils/helpers';
import PrimaryButton from 'atoms/buttons/PrimaryButton';
interface PromoCodeProps {
  currencyCharCode: any;
  currencyCode: any;
  discountAmount: any;
  setDiscount: any;
  setDiscountAmount: any;
  discount: any;
  profile: any;
  selectedPackage: any;
  applyPromoCode: any;
  setSelectedPackageData: any;
  taxCountry: any;
  paymentChannel?: string | null;
}

const PromoCode = ({
  currencyCharCode,
  currencyCode,
  discountAmount,
  discount,
  setDiscount,
  setDiscountAmount,
  profile,
  selectedPackage,
  applyPromoCode,
  setSelectedPackageData,
  taxCountry,
  paymentChannel = null,
}: PromoCodeProps): ReactElement => {
  const [promoCode, setPromoCode] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState({ status: 'default', message: '' });

  const isMobile = useMediaBreakpoints();

  const renderDiscount = discount => {
    if (discount) {
      if (discount.type === 'percentage' && discount.discount_rate) {
        const discountRate = Math.floor(discount.discount_rate * 100);

        return `${discountRate}% OFF promo applied!`;
      }

      if (discount.type === 'fixed' && discount.discount_amount) {
        return `${String.fromCharCode(currencyCharCode)}${discount.discount_amount} OFF promo applied!`;
      }
    }

    return 'Discount Amount';
  };

  const handleApplyPromoCode = e => {
    e.preventDefault();
    setProcessing(true);

    handlePaymentVysionEvent({
      action: VYSION_ACTIONS.CLICK,
      event: PaymentClickVysionEventsEnum.CLICK_PROMO_CODE_APPLY,
    });

    promoCodeCheck(
      profile.profile.data.hash_id,
      promoCode,
      selectedPackage.identifier,
      false,
      taxCountry,
      true,
      paymentChannel
    )
      .then(response => {
        if (response.data === null) {
          setError({
            status: 'error',
            message: 'Invalid promo code',
          });
          setProcessing(false);
          return;
        }

        if (response.data.discount_rate) {
          const discountRate = response.data.discount_rate;

          discountAmount = selectedPackage.price * discountRate;
        }

        if (response.data.discount_amount) {
          discountAmount = response.data.discount_amount;
        }

        if (response.data.package) {
          setSelectedPackageData(response.data.package.data);
        }

        setDiscount(response.data);
        setDiscountAmount(discountAmount);
        applyPromoCode(promoCode);
        setProcessing(false);
      })
      .catch(() => {
        setProcessing(false);
        setError({
          status: 'error',
          message: 'Error checking promo package.',
        });
      });
  };

  if (parseFloat(discountAmount) > 0) {
    return (
      <DiscountDetails>
        <DiscountText>{renderDiscount(discount)}</DiscountText>
        <SummaryAmountText>
          - {String.fromCharCode(currencyCharCode)}
          {parseFloat(discountAmount).toFixed(2)} {currencyCode}
        </SummaryAmountText>
      </DiscountDetails>
    );
  }

  return (
    <PromoCodeContainer>
      <PromoInputContainer>
        <TextInput
          id="promo_code"
          type="text"
          placeholder="Enter Promo Code"
          value={promoCode}
          onFocus={() => {
            handlePaymentVysionEvent({
              action: VYSION_ACTIONS.ON_FOCUS,
              event: PaymentOnFocusVysionEventsEnum.PROMO_CODE,
            });
          }}
          customStyle={{
            width: 200,
            borderRadius: '3px',
            paddingBottom: 0,
            marginRight: 0,
          }}
          customInputStyle={{
            borderRadius: '3px !important',
            padding: '10px 14px  !important',
            boxShadow: `1px 1px 3px 0px ${isDarkMode() ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.2)'} inset`,
            border: (error.status === 'error' ? '1px solid #F00' : `${getBorders().PRIMARY}`) + ' !important',
            background: getBackgroundTheme().block3 + ' !important',
          }}
          onChange={e => {
            if (e.target.value.length <= 0) {
              setError({ status: 'default', message: '' });
            }

            setPromoCode(e.target.value);
          }}
        />
        {error.status === 'error' && !isMobile && <ErrorDisplay>{error.message}</ErrorDisplay>}
      </PromoInputContainer>
      {error.status === 'error' && isMobile && <ErrorDisplay>{error.message}</ErrorDisplay>}
      <PrimaryButton
        disabled={error.status === 'error' || promoCode.length <= 0 || processing}
        color={error.status === 'error' || promoCode.length <= 0 || processing ? 'red' : 'blue'}
        size="x-small"
        data-test-id="close-first-date-offer-modal"
        onClick={handleApplyPromoCode}
        label="Apply"
        customStyle={{
          width: 'inherit',
          borderRadius: 4,
          fontSize: 13,
          fontWeight: 600,
          lineHeight: '16px',
          letterSpacing: -0.26,
          padding: '6px 16px',
          ...((error.status === 'error' || promoCode.length <= 0 || processing) && isDarkMode()
            ? {
                color: '#212121',
                backgroundColor: '#585858',
                border: '2px solid #585858',
              }
            : {}),
        }}
      />
    </PromoCodeContainer>
  );
};

export default PromoCode;
